import { baseFetch } from '@gumtree/shared/src/util/base-fetch';
import { getGA4ClientId } from '@gumtree/shared/src/util/ga4-shared';
import { clearAllByKey, loadByKey, removeSingleByKey, saveByKey } from './recent-service';

export const RECENT_LOCATIONS_KEY = 'recentLocations';
const SESSION_STORAGE_KEY = 'recentLocations_apiFetched';
const SESSION_CACHE_KEY = 'recentLocations_cache';
const STORAGE_MAX = 10;

export type RecentLocationsItem = {
    name: string;
    type?: string;
    id?: string;
};

export interface RecentLocationRecord {
    location_id: string;
    location_name: string;
    location_type: string;
    create_ts: number;
    latitude?: number;
    longitude?: number;
}

export interface RecentLocationsApiResponse {
    code: number;
    message: string;
    data: {
        locations: RecentLocationRecord[];
    } | null;
}

/**
 * Get cache from sessionStorage (persists across page loads)
 */
const getSessionCache = (): RecentLocationsItem[] | null => {
    try {
        const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
        return cached ? JSON.parse(cached) : null;
    } catch {
        return null;
    }
};

/**
 * Save cache to sessionStorage (persists across page loads)
 */
const setSessionCache = (data: RecentLocationsItem[]) => {
    try {
        sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(data));
    } catch {
        // Ignore
    }
};

/**
 * Check if we already fetched API this browser session
 */
const hasApiFetchedThisSession = (): boolean => {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Mark that we fetched API this session
 */
const markApiFetched = () => {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    } catch {
        // Ignore
    }
};

/**
 * Clear API fetched flag (for login events)
 */
const clearApiFetchedFlag = () => {
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_CACHE_KEY);
    } catch {
        // Ignore
    }
};

/**
 * Save a new location - optimistically updates cache
 */
export const save = (locations: RecentLocationsItem) => {
    console.log('[Recent Locations] 💾 Saving:', locations.name);

    const cachedData = getSessionCache();

    if (hasApiFetchedThisSession() && cachedData) {
        // Optimistic update: add to session cache
        const updated = [
            locations,
            ...cachedData.filter((item) => item.name !== locations.name),
        ].slice(0, STORAGE_MAX);
        setSessionCache(updated);
        console.log('[Recent Locations] ✅ Optimistically added to session cache');
    } else {
        // Remove any existing entry with the same name first (saveByKey uses deep equality,
        // which would treat { name: "London", type: "Area" } and { name: "London" } as different)
        removeSingleByKey(RECENT_LOCATIONS_KEY, locations.name);
        // Not using API data, save to localStorage
        saveByKey(RECENT_LOCATIONS_KEY, locations, STORAGE_MAX);
        console.log('[Recent Locations] ✅ Saved to localStorage');
    }
};

export const load = (limit = 5) => loadByKey(RECENT_LOCATIONS_KEY, limit);

/**
 * Load recent locations - API first for logged-in users (once per session), then cache
 */
export const loadWithApiSupport = async (
    userData: { userLoggedIn: boolean; id: number } | null,
    limit = 5
): Promise<RecentLocationsItem[]> => {
    const cachedData = getSessionCache();

    console.log('[Recent Locations] 🔍 Load called:', {
        hasSessionCache: !!cachedData,
        hasFetchedThisSession: hasApiFetchedThisSession(),
        userLoggedIn: userData?.userLoggedIn,
    });

    // FIRST: If we have session cache (from API), return it
    if (cachedData && hasApiFetchedThisSession()) {
        console.log('[Recent Locations] ⚡ Using session cache from previous page');
        return cachedData.filter((item) => !!item.name).slice(0, limit);
    }

    // SECOND: Check if user is logged in AND we haven't fetched this session yet
    const userId = userData?.id?.toString() ?? getGA4ClientId();

    if (userData?.userLoggedIn && userId && !hasApiFetchedThisSession()) {
        try {
            console.log('[Recent Locations] 📡 Fetching from API (first time this session)...', {
                userId,
                isGAClientId: !userData?.id,
            });
            const response = (await baseFetch(
                `/bff-api/users/get_recent_records?user_id=${userId}&action_type=location`,
                {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                    },
                }
            )) as RecentLocationsApiResponse;

            // Mark as fetched IMMEDIATELY to prevent duplicate calls
            markApiFetched();

            // code 0 = success with data, code 20401 = no records found (treat as empty, not an error)
            const isSuccessOrEmpty = response.code === 0 || response.code === 20401;

            if (isSuccessOrEmpty) {
                // API responded - always replace localStorage to clear any pre-login history
                const locations = (response.data?.locations ?? [])
                    .filter((location) => !!location.location_name)
                    .map((location) => ({
                        name: location.location_name,
                        type: location.location_type,
                        id: location.location_id,
                    }));

                console.log(
                    '[Recent Locations] ✅ API responded! Caching',
                    locations.length,
                    'locations (code:',
                    response.code,
                    ')'
                );

                // Update session cache
                setSessionCache(locations);

                // Always replace localStorage with API response - this clears any pre-login history (e.g. Coventry)
                console.log(
                    '[Recent Locations] 💾 Replacing localStorage with API data (clearing pre-login history)'
                );
                clearAllByKey(RECENT_LOCATIONS_KEY);
                locations.forEach((location, index) => {
                    if (index < STORAGE_MAX) {
                        saveByKey(RECENT_LOCATIONS_KEY, location, STORAGE_MAX);
                    }
                });

                return locations.slice(0, limit);
            } else {
                console.warn(
                    '[Recent Locations] ⚠️ API returned unexpected error code:',
                    response.code
                );
            }
        } catch (error) {
            console.error('[Recent Locations] ❌ API failed:', error);
            markApiFetched(); // Still mark as fetched to prevent retry
        }
    } else if (hasApiFetchedThisSession()) {
        console.log(
            '[Recent Locations] ⏭️ Already fetched API this session, using localStorage fallback'
        );
    }

    // THIRD: Fallback to localStorage
    console.log('[Recent Locations] 📦 Using localStorage fallback');
    return loadByKey(RECENT_LOCATIONS_KEY, limit);
};

const broadcastChange = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('recentLocationsChanged'));
    }
};

export const removeSingleFromHistory = (text: string) => {
    const cachedData = getSessionCache();

    if (cachedData && hasApiFetchedThisSession()) {
        // Update session cache
        const updated = cachedData.filter((item) => item.name !== text);
        setSessionCache(updated);
    }
    // Always remove from localStorage as well to keep both stores in sync
    removeSingleByKey(RECENT_LOCATIONS_KEY, text);
    broadcastChange();
};

export const clearAllFromHistory = () => {
    if (hasApiFetchedThisSession()) {
        // Clear session cache
        setSessionCache([]);
    }
    // Always clear localStorage as well to keep both stores in sync
    clearAllByKey(RECENT_LOCATIONS_KEY);
    broadcastChange();
};

/**
 * Force refresh on login
 */
export const forceApiRefresh = () => {
    console.log('[Recent Locations] 🔄 Force refresh - clearing session data');
    clearApiFetchedFlag();
};

const PREFERRED_LOCATION_CACHE_KEY = 'gt_srp4_preferredLocation';

const getUserPrefCookieDomain = (): string => {
    const hostname = window.location.hostname;
    const domainParts = hostname.split('.');
    return domainParts.length > 2 ? '.' + domainParts.slice(-2).join('.') : hostname;
};

/**
 * Remove the `location:` segment from the `gt_userPref` cookie.
 * Called immediately to prevent stale SEO-derived locations from persisting.
 */
export const clearLocationFromUserPrefCookie = () => {
    if (typeof document === 'undefined') return;

    const allCookies = document.cookie.split('; ');
    const userPrefCookie = allCookies.find((c) => c.startsWith('gt_userPref='));

    if (!userPrefCookie) return;

    const value = userPrefCookie.substring('gt_userPref='.length);
    const parts = value.split('|');
    const filteredParts = parts.filter((part) => !part.startsWith('location:'));

    if (filteredParts.length === parts.length) return;

    const newValue = filteredParts.join('|');
    const domain = getUserPrefCookieDomain();
    document.cookie = `gt_userPref=${newValue}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
};

/**
 * Write the user's preferred location into the `gt_userPref` cookie so the
 * Buyer backend returns it as `model.core.searchLocation` on the next page load.
 * This prevents the "United Kingdom" → actual location flash.
 */
export const updateLocationInUserPrefCookie = (locationName: string) => {
    if (typeof document === 'undefined') return;

    let encodedLocation: string;
    try {
        encodedLocation = btoa(locationName);
    } catch {
        return;
    }

    const domain = getUserPrefCookieDomain();
    const allCookies = document.cookie.split('; ');
    const userPrefCookie = allCookies.find((c) => c.startsWith('gt_userPref='));

    if (!userPrefCookie) {
        document.cookie = `gt_userPref=location:${encodedLocation}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
        return;
    }

    const value = userPrefCookie.substring('gt_userPref='.length);
    const parts = value.split('|');
    const hasLocation = parts.some((part) => part.startsWith('location:'));

    const updatedParts = hasLocation
        ? parts.map((part) => (part.startsWith('location:') ? `location:${encodedLocation}` : part))
        : [...parts, `location:${encodedLocation}`];

    const newValue = updatedParts.join('|');
    document.cookie = `gt_userPref=${newValue}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
};

/**
 * Set a custom cookie with the user's preferred location.
 * Unlike gt_userPref (which the Buyer backend overwrites via Set-Cookie on every
 * response), this cookie is under our control and persists across page loads.
 * The BFF reads it server-side and injects it into gt_userPref before forwarding
 * the request to the Buyer, so the server renders with the correct location.
 */
export const setPreferredLocationCookie = (locationName: string) => {
    if (typeof document === 'undefined') return;
    const domain = getUserPrefCookieDomain();
    document.cookie = `gt_srp4_loc=${encodeURIComponent(
        locationName
    )}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
};

/**
 * Cache the user's preferred location in localStorage so it can be read
 * synchronously in useLayoutEffect to prevent the flash on the current page load.
 */
export const cachePreferredLocation = (name: string) => {
    try {
        localStorage.setItem(PREFERRED_LOCATION_CACHE_KEY, name);
    } catch {
        // Ignore
    }
};

export const getCachedPreferredLocation = (): string | null => {
    try {
        return localStorage.getItem(PREFERRED_LOCATION_CACHE_KEY);
    } catch {
        return null;
    }
};

// Debug helper
if (typeof window !== 'undefined') {
    (window as any).debugRecentLocations = {
        getState: () => ({
            sessionCache: getSessionCache(),
            hasApiFetchedThisSession: hasApiFetchedThisSession(),
            sessionStorageFlag: sessionStorage.getItem(SESSION_STORAGE_KEY),
        }),
        reset: () => {
            clearApiFetchedFlag();
            console.log('[Recent Locations] 🔄 Full reset');
        },
    };
}
